import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  HostListener,
  Optional,
  Self,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  // Added by Amisha on 2026-09-15. Reason: hold the applicant panel template for the overlay.
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
// Added by Amisha on 2026-09-15. Reason: render only an overlapping applicant panel above the header.
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CommonDropdownData, DropdownOption } from './common-dropdown.component.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'wm-common-dropdown',
  templateUrl: './common-dropdown.component.html',
  styleUrls: ['./common-dropdown.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommonDropdownComponent implements ControlValueAccessor, OnInit, OnDestroy  {
  private static instances: CommonDropdownComponent[] = [];
  // Added by Amisha on 2026-06-19.
  // Reason: the Janus exports menu must keep the Figma width while treating the
  // design height as a max cap so small menus do not render excess whitespace.
  private static readonly JANUS_EXPORT_PANEL_WIDTH = 346;
  private static readonly JANUS_EXPORT_PANEL_MAX_HEIGHT = 397;
  private static readonly JANUS_EXPORT_PANEL_GAP = 6;
  private static readonly JANUS_EXPORT_VIEWPORT_MARGIN = 16;
  // @Input({ required: true }) data!: CommonDropdownData;
  @Output() change = new EventEmitter<string | number | null>();
  _value: string | number = '';
   private _data: CommonDropdownData;
  private _valueSubscription?: Subscription;
  open = false;
  panelStyles: Record<string, string> = {};
  // Added by Amisha on 2026-09-15. Reason: track the applicant panel while it is rendered outside the form.
  useApplicantOverlay = false;
  @ViewChild('dropdownPanelTemplate') dropdownPanelTemplate!: TemplateRef<unknown>;
  private applicantPanelOverlay?: OverlayRef;
  private janusPanelIsConstrained = false;
  private listensForCapturedScroll = false;
  private readonly capturedScrollHandler = (): void => {
    if (this.open && this.data?.closeOnScroll === true) {
      this.close();
    }
  };

   @Input({ required: true })
    set data(value: CommonDropdownData) {
      this._data = { ...this._data, ...value};
      if (value && value.selectedValue !== undefined) {
        this._value = value.selectedValue;
      }
      this.cdr.markForCheck();
      // this.syncValueFromData();
    }

    get data(): CommonDropdownData {
      return this._data;
    }


    @Output() valueChange = new EventEmitter<any>();


  onChange = (_: any) => {};
  onTouched = () => {};
  // added by Vipul Rathod on 17/07/2026
  @Output()
  checkboxChange = new EventEmitter<{
      itemId:string;
      checkboxIndex:number;
      checked:boolean;
  }>();

  @Output()
  selectAllChange = new EventEmitter<{
      checkboxIndex:number;
      checked:boolean;
  }>();
  // end by Vipul Rathod on 17/07/2026

  // Added by Vipul Rathod on 24/07/2026
  private lockedScrollParents: Array<{
    element: HTMLElement;
    overflow: string;
    overflowX: string;
    overflowY: string;
  }> = [];
  // End by Vipul Rathod on 24/07/2026

  constructor(
    private elRef: ElementRef,
    @Optional() @Self() public ngControl: NgControl,
    private cdr: ChangeDetectorRef,
    // Added by Amisha on 2026-09-15. Reason: create a portal for the applicant panel only when it crosses the header.
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }


  ngOnInit(): void {
    this.setupControlSubscription();
    CommonDropdownComponent.instances.push(this);
    if (this.data?.closeOnScroll === true) {
      // Added by Amisha on 09/25/2026. Reason: nested-container scroll events
      // do not bubble; register only for opt-in dropdowns to avoid affecting
      // the many existing shared-dropdown instances.
      document.addEventListener('scroll', this.capturedScrollHandler, true);
      this.listensForCapturedScroll = true;
    }
  }

  private setupControlSubscription(): void {
    if (this.ngControl?.control) {

      if (
        (this.ngControl.control.value === null ||
         this.ngControl.control.value === '') &&
        this._data.selectedValue !== null
      ) {
        this.ngControl.control.setValue(this._data.selectedValue, { emitEvent: false });
        this._value = this._data.selectedValue;
      }

      this._valueSubscription =
        this.ngControl.control.valueChanges.subscribe(val => {
          const newValue = val ?? null;
          if (this._value !== newValue) {
            this._value = newValue;
            this.valueChange.emit(newValue);
          }
        });
    }
  }

  ngOnDestroy(): void {
    // Added by Amisha on 2026-09-15. Reason: release an open applicant portal when this dropdown is destroyed.
    this.closeApplicantPanelOverlay();
    // Added by Amisha on 09/25/2026. Reason: restore locked ancestors when an
    // open dropdown is destroyed as its containing modal closes.
    this.unlockParentScrolling();
    if (this.listensForCapturedScroll) {
      document.removeEventListener('scroll', this.capturedScrollHandler, true);
      this.listensForCapturedScroll = false;
    }
    this._valueSubscription?.unsubscribe();
    CommonDropdownComponent.instances = CommonDropdownComponent.instances.filter(i => i !== this);
  }

  get selectedLabel(): string {
    const found = (this.data?.options || []).find(o => o.value === this._value);
    return found?.label || '';
  }

  // Added by Amisha on 2026-09-15. Reason: limit the header fix to list dropdowns in applicant home.
  private isApplicantFormDropdown(): boolean {
    const element = this.elRef.nativeElement as HTMLElement;
    return !!element.closest('wm-app-applicant-home') &&
      !!element.closest('wm-list-data-entity-input');
  }

  // Added by Amisha on 2026-09-15. Reason: move the panel only when its viewport position crosses the applicant header.
  private applicantPanelOverlapsHeader(): boolean {
    if (!this.isApplicantFormDropdown()) return false;

    const header = document.querySelector('.header-parent-container') as HTMLElement | null;
    const panel = this.elRef.nativeElement.querySelector('.dropdown-panel') as HTMLElement | null;
    if (!header || !panel) return false;

    const headerRect = header.getBoundingClientRect();
    const top = Number.parseFloat(this.panelStyles.top);
    const maxHeight = Number.parseFloat(this.panelStyles.maxHeight);
    const height = Math.min(panel.scrollHeight, maxHeight);
    return Number.isFinite(top) && Number.isFinite(height) &&
      top < headerRect.bottom && top + height > headerRect.top;
  }

  // Added by Amisha on 2026-09-15. Reason: put only the panel above the header while the form remains below it.
  private openApplicantPanelOverlay(): void {
    this.applicantPanelOverlay = this.overlay.create({
      positionStrategy: this.overlay.position().global().top('0').left('0'),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false
    });
    this.applicantPanelOverlay.attach(
      new TemplatePortal(this.dropdownPanelTemplate, this.viewContainerRef)
    );
  }

  // Added by Amisha on 2026-09-15. Reason: dispose the panel portal and restore inline rendering after close.
  private closeApplicantPanelOverlay(): void {
    this.applicantPanelOverlay?.dispose();
    this.applicantPanelOverlay = undefined;
    this.useApplicantOverlay = false;
  }

  // Added by Amisha on 2026-06-15.
  // Reason: keep the Janus scheduled export dropdown behavior opt-in through a
  // dedicated class name so shared dropdown consumers continue using the default flow.
  get isJanusExportMenu(): boolean {
    return this.data?.className === 'janus-export-menu-dropdown';
  }

  // Added by Amisha on 2026-07-23.
  // Reason: the contractor profile registration modal must commit an option
  // before its inline-overlay focus handling can swallow the first click.
  get selectsOnPointerDown(): boolean {
    return this.data?.className === 'new-registration-common-dropdown';
  }

  // Added by Amisha on 2026-06-15.
  // Reason: Janus exports need a dedicated scrolling middle section while the top
  // format actions and bottom create action remain fixed within the shared dropdown.
  get janusTopOptions(): DropdownOption[] {
    return (this.data?.options || []).filter(option =>
      option.className?.includes('top-option') ||
      option.className?.includes('top-divider')
    );
  }

  get janusMiddleOptions(): DropdownOption[] {
    return (this.data?.options || []).filter(option =>
      option.className?.includes('scheduled-export-option')
    );
  }

  get janusBottomOptions(): DropdownOption[] {
    return (this.data?.options || []).filter(option =>
      option.className?.includes('bottom-divider') ||
      option.className?.includes('bottom-option') ||
      option.className?.includes('create-new-option')
    );
  }

  get hasJanusMiddleOptions(): boolean {
    return this.janusMiddleOptions.length > 0;
  }

  get hasJanusScrollableMiddle(): boolean {
    return this.hasJanusMiddleOptions && this.janusPanelIsConstrained;
  }

  get dropdownPanelStyles(): Record<string, string | null> {
    // Added by Amisha on 2026-06-19.
    // Reason: Janus exports need runtime top/left/height overrides while standard
    // shared dropdowns should continue using the existing width and height bindings.
    return {
      background: this.data?.style?.panelBg || null,
      boxShadow: this.data?.style?.panelShadow || null,
      minWidth: this.isJanusExportMenu ? null : (this.data?.style?.width || '100%'),
      height: !this.isJanusExportMenu ? (this.data?.panelHeight || null) : null,
      ...this.panelStyles
    };
  }

  toggle(event: MouseEvent) {
    event.stopPropagation();
    if (this.data?.disabled) return;
    if (!this.open) {
      CommonDropdownComponent.instances.forEach(instance => {
        if (instance !== this) {
          instance.close();
        }
      });
    }

    this.open = !this.open;

    // Added by Amisha on 2026-06-19.
    // Reason: Janus exports needs the panel DOM rendered first so we can compute a
    // fixed viewport position that flips up when the lower space is too small.
    if (this.open && this.isJanusExportMenu) {
      setTimeout(() => this.updateJanusExportPanelPosition());
    }

    // Added by Vipul Rathod on 23/07/2026
    if (this.open) {
      if (this.data?.lockParentScroll !== false) {
        this.lockParentScrolling();
      }
      this.cdr.detectChanges();

      this.updateDropdownPosition();
      // Added by Amisha on 2026-09-15. Reason: portal only applicant list panels that overlap the header.
      if (this.applicantPanelOverlapsHeader()) {
        this.useApplicantOverlay = true;
        this.cdr.detectChanges();
        this.openApplicantPanelOverlay();
        this.updateDropdownPosition();
      }
    }
    // Added by Vipul Rathod on 23/07/2026

    if (!this.open) {
      // Added by Amisha on 2026-09-15. Reason: remove the portal when the trigger closes the panel.
      this.closeApplicantPanelOverlay();
      this.unlockParentScrolling(); // Added by Vipul Rathod on 24/07/2026
      this.panelStyles = {};
      this.janusPanelIsConstrained = false;
      this.onTouched();
    }

    this.cdr.markForCheck();
  }

  close(): void {
    this.open = false;
    // Added by Amisha on 2026-09-15. Reason: remove the portal when another dropdown opens.
    this.closeApplicantPanelOverlay();
    this.unlockParentScrolling(); // Added by Vipul Rathod on 24/07/2026
    this.panelStyles = {};
    this.janusPanelIsConstrained = false;
    this.cdr.markForCheck();
  }

  select(option: DropdownOption) {
    // Added by Amisha on 2026-06-15.
    // Reason: divider rows and disabled Janus export options should never update the shared value.
    if (this.data?.disabled || option?.disabled) return;

    this.data.selectedValue = option.value;
    this.open = false;
    // Added by Amisha on 2026-09-15. Reason: remove the portal after selecting an option.
    this.closeApplicantPanelOverlay();
    this.unlockParentScrolling(); // Added by Vipul Rathod on 24/07/2026

    if (!this._data?.actionMode) {
      this._value = option.value;
      this.onChange(option.value);
    }
    this.onTouched();

    this.change.emit(option.value);

    this.valueChange.emit(option.value);
  }

  // Added by Amisha on 2026-07-23.
  // Reason: provide an opt-in first-pointer selection path for the new
  // contractor registration dropdowns without changing other consumers.
  selectOnPointerDown(event: PointerEvent, option: DropdownOption): void {
    if (!this.selectsOnPointerDown || this.data?.disabled || option?.disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.data.selectedValue = option.value;

    if (!this._data?.actionMode) {
      this._value = option.value;
      this.onChange(option.value);
    }

    this.open = false;
    // Added by Amisha on 2026-09-15. Reason: remove the portal after pointer selection.
    this.closeApplicantPanelOverlay();
    this.onTouched();
    this.change.emit(option.value);
    this.valueChange.emit(option.value);
    this.cdr.markForCheck();
  }

  completePointerSelection(event: MouseEvent, option: DropdownOption): void {
    if (!this.selectsOnPointerDown) {
      this.select(option);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.data?.disabled || option?.disabled) {
      return;
    }

    this.open = false;
    // Added by Amisha on 2026-09-15. Reason: remove the portal after completing pointer selection.
    this.closeApplicantPanelOverlay();
    this.onTouched();
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      if (this.open) {
        this.onTouched();
      }
      this.open = false;
      // Added by Amisha on 2026-09-15. Reason: remove the portal when clicking outside the dropdown.
      this.closeApplicantPanelOverlay();
      this.unlockParentScrolling(); // Added by Vipul Rathod on 24/07/2026
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    // Added by Amisha on 2026-06-19.
    // Reason: Janus export panels should stay clamped inside the viewport after the
    // browser size changes instead of keeping stale fixed coordinates.
    if (this.open && this.isJanusExportMenu) {
      this.updateJanusExportPanelPosition();
    }

    // Added by Vipul Rathod on 23/07/2026
    if (this.open) {
      this.cdr.detectChanges();

      this.updateDropdownPosition();
    }
    // End by Vipul Rathod on 23/07/2026
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    // Added by Amisha on 2026-06-19.
    // Reason: the Janus export menu is positioned using viewport coordinates, so it
    // must be recomputed when the page scroll position changes.
    if (this.open && this.isJanusExportMenu) {
      this.updateJanusExportPanelPosition();
    }

    // Added by Vipul Rathod on 23/07/2026
    if (this.open) {
      this.cdr.detectChanges();

      this.updateDropdownPosition();
    }
    // End by Vipul Rathod on 23/07/2026
  }


  writeValue(value: any): void {
    this._value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.data) {
      this.data.disabled = isDisabled;
    }
  }

  private updateJanusExportPanelPosition(): void {
    // Added by Amisha on 2026-06-19.
    // Reason: the Janus exports dropdown must open upward when there is more room
    // above, shrink to the visible viewport height, and stay inside left/right bounds.
    if (!this.open || !this.isJanusExportMenu) {
      return;
    }

    const anchorRect = this.elRef.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = CommonDropdownComponent.JANUS_EXPORT_VIEWPORT_MARGIN;
    const gap = CommonDropdownComponent.JANUS_EXPORT_PANEL_GAP;
    const desiredWidth = CommonDropdownComponent.JANUS_EXPORT_PANEL_WIDTH;
    const desiredMaxHeight = CommonDropdownComponent.JANUS_EXPORT_PANEL_MAX_HEIGHT;
    const panelElement = this.elRef.nativeElement.querySelector('.dropdown-panel') as HTMLElement | null;
    const rawContentHeight = panelElement?.scrollHeight || desiredMaxHeight;
    const naturalHeight = Math.min(
      rawContentHeight,
      desiredMaxHeight
    );

    const panelWidth = Math.min(desiredWidth, Math.max(0, viewportWidth - margin * 2));
    const spaceBelow = Math.max(0, viewportHeight - anchorRect.bottom - gap - margin);
    const spaceAbove = Math.max(0, anchorRect.top - gap - margin);
    const openUpward = spaceBelow < naturalHeight && spaceAbove > spaceBelow;
    const availableHeight = openUpward ? spaceAbove : spaceBelow;
    const panelMaxHeight = Math.min(
      naturalHeight,
      Math.max(0, availableHeight),
      Math.max(0, viewportHeight - margin * 2)
    );
    const renderedHeight = Math.min(naturalHeight, panelMaxHeight || naturalHeight);
    const shouldConstrainPanel = rawContentHeight > renderedHeight;
    this.janusPanelIsConstrained = shouldConstrainPanel;

    const unclampedLeft = anchorRect.right - panelWidth;
    const maxLeft = Math.max(margin, viewportWidth - margin - panelWidth);
    const left = Math.min(Math.max(unclampedLeft, margin), maxLeft);

    const top = openUpward
      ? Math.max(margin, anchorRect.top - gap - renderedHeight)
      : Math.min(
          Math.max(margin, anchorRect.bottom + gap),
          Math.max(margin, viewportHeight - margin - renderedHeight)
        );

    this.panelStyles = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      right: 'auto',
      bottom: 'auto',
      width: `${panelWidth}px`,
      minWidth: `${panelWidth}px`,
      maxWidth: `${panelWidth}px`,
      maxHeight: `${panelMaxHeight}px`,
      height: shouldConstrainPanel ? `${renderedHeight}px` : 'auto',
      minHeight: '0'
    };

    this.cdr.markForCheck();
  }

  // Added by Vipul Rathod on 22-07-2026
  private updateDropdownPosition(): void {

    if (!this.open) {
      return;
    }

    const dropdownControl = this.elRef.nativeElement.querySelector(
        '.dropdown-control'
    ) as HTMLElement;

    const anchorRect = dropdownControl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = CommonDropdownComponent.JANUS_EXPORT_VIEWPORT_MARGIN;
    const gap = CommonDropdownComponent.JANUS_EXPORT_PANEL_GAP;
    const desiredMaxHeight = CommonDropdownComponent.JANUS_EXPORT_PANEL_MAX_HEIGHT;
    // Added by Amisha on 2026-09-15. Reason: measure the portaled panel without changing other panel positioning.
    const panelElement = (this.applicantPanelOverlay?.overlayElement ||
      this.elRef.nativeElement).querySelector('.dropdown-panel') as HTMLElement | null;
    const rawContentHeight = panelElement?.scrollHeight || desiredMaxHeight;
    const naturalHeight = Math.min(
      rawContentHeight,
      desiredMaxHeight
    );

    const spaceBelow = Math.max(0, viewportHeight - anchorRect.bottom - gap - margin);
    const spaceAbove = Math.max(0, anchorRect.top - gap - margin);
    const openUpward = spaceBelow < naturalHeight && spaceAbove > spaceBelow;
    const availableHeight = openUpward ? spaceAbove : spaceBelow;
    const panelMaxHeight = Math.min(
      naturalHeight,
      Math.max(0, availableHeight),
      Math.max(0, viewportHeight - margin * 2)
    );
    const renderedHeight = Math.min(naturalHeight, panelMaxHeight || naturalHeight);
    const shouldConstrainPanel = rawContentHeight > renderedHeight;

    const top = openUpward
      ? Math.max(margin, anchorRect.top - gap - renderedHeight)
      : Math.min(
          Math.max(margin, anchorRect.bottom + gap),
          Math.max(margin, viewportHeight - margin - renderedHeight)
        );

    this.panelStyles = {
      position: 'fixed',
      top: `${top}px`,
      left: `${anchorRect.left}px`,
      right: 'auto',
      bottom: 'auto',
      width: `${anchorRect.width}px`,
      maxHeight: `${panelMaxHeight}px`,
      height: shouldConstrainPanel ? `${renderedHeight}px` : 'auto',
      minHeight: '0'
    };

    this.cdr.markForCheck();
  }
  // Added by Vipul Rathod on 22-07-2026
  // Added by Vipul Rathod on 17/07/2026
  checkboxChanged(
    itemId:string,
    checkboxIndex:number,
    checked:boolean
  ){
      this.checkboxChange.emit({
          itemId,
          checkboxIndex,
          checked
      });
  }

  selectAllChanged(
      checkboxIndex:number,
      checked:boolean
  ){
      this.selectAllChange.emit({
          checkboxIndex,
          checked
      });
  }
  // end by Vipul Rathod on 17/07/2026

  // Added by Vipul Rathod on 24/07/2026
  private getScrollableParents(element: HTMLElement): HTMLElement[] {
    const parents: HTMLElement[] = [];

    let parent = element.parentElement;

    while (parent) {
      const style = getComputedStyle(parent);

      const isScrollable =
        /(auto|scroll|overlay)/.test(style.overflow) ||
        /(auto|scroll|overlay)/.test(style.overflowY) ||
        /(auto|scroll|overlay)/.test(style.overflowX);

      if (isScrollable) {
        parents.push(parent);
      }

      parent = parent.parentElement;
    }

    return parents;
  }

  private lockParentScrolling(): void {
    this.lockedScrollParents = [];

    const parents = this.getScrollableParents(this.elRef.nativeElement);

    parents.forEach(parent => {
      this.lockedScrollParents.push({
        element: parent,
        overflow: parent.style.overflow,
        overflowX: parent.style.overflowX,
        overflowY: parent.style.overflowY
      });

      parent.style.overflow = 'hidden';
    });
  }

  private unlockParentScrolling(): void {

    this.lockedScrollParents.forEach(item => {

      item.element.style.overflow = item.overflow;
      item.element.style.overflowX = item.overflowX;
      item.element.style.overflowY = item.overflowY;

    });

    this.lockedScrollParents = [];
  }
  // End by Vipul Rathod on 24/07/2026
}
